'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ScheduledPost } from './ContentCalendarContent';
import Badge from '@/components/ui/Badge';

interface Props {
  posts: ScheduledPost[];
  onCreatePost: () => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export default function CalendarGrid({ posts, onCreatePost }: Props) {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(7); // August = 7
  const [selectedPost, setSelectedPost] = useState<ScheduledPost | null>(null);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const monthName = new Date(year, month, 1).toLocaleString('en-US', { month: 'long' });

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  const getPostsForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return posts.filter((p) => p.scheduledDate === dateStr);
  };

  const today = { year: 2026, month: 7, day: 6 };
  const isToday = (day: number) => year === today.year && month === today.month && day === today.day;

  const cells = Array.from({ length: firstDay + daysInMonth }).map((_, i) => {
    if (i < firstDay) return null;
    return i - firstDay + 1;
  });

  // Pad to complete rows
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="card-base overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all">
            <ChevronLeft size={16} />
          </button>
          <h3 className="text-base font-semibold text-foreground">{monthName} {year}</h3>
          <button onClick={nextMonth} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-facebook inline-block" />
            Facebook
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-instagram inline-block" />
            Instagram
          </div>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {DAYS.map((d) => (
          <div key={`day-header-${d}`} className="px-2 py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar cells */}
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          const dayPosts = day ? getPostsForDay(day) : [];
          const isCurrentDay = day ? isToday(day) : false;
          return (
            <div
              key={`cell-${idx}`}
              className={`
                min-h-[100px] p-2 border-r border-b border-border last:border-r-0
                ${!day ? 'bg-muted/20' : 'hover:bg-muted/20 cursor-pointer'}
                ${idx % 7 === 6 ? 'border-r-0' : ''}
              `}
              onClick={() => day && onCreatePost()}
            >
              {day && (
                <>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mb-1.5 ${isCurrentDay ? 'bg-primary text-primary-foreground' : 'text-foreground'}`}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {dayPosts.slice(0, 3).map((post) => (
                      <button
                        key={post.id}
                        onClick={(e) => { e.stopPropagation(); setSelectedPost(post); }}
                        className={`
                          w-full text-left px-1.5 py-1 rounded text-[10px] font-medium truncate transition-all duration-150
                          ${post.platform === 'facebook' ? 'bg-facebook-bg text-facebook hover:bg-facebook/20' : 'bg-instagram-bg text-instagram hover:bg-instagram/20'}
                          ${post.status === 'failed' ? 'opacity-60 line-through' : ''}
                        `}
                      >
                        {post.scheduledTime} · {post.title}
                      </button>
                    ))}
                    {dayPosts.length > 3 && (
                      <p className="text-[10px] text-muted-foreground px-1">+{dayPosts.length - 3} more</p>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Post Detail Panel */}
      {selectedPost && (
        <div className="border-t border-border p-5 bg-muted/30 fade-in">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={selectedPost.platform}>{selectedPost.platform === 'facebook' ? 'Facebook' : 'Instagram'}</Badge>
                <Badge variant={selectedPost.status}>{selectedPost.status}</Badge>
                <span className="text-xs text-muted-foreground">{selectedPost.type}</span>
              </div>
              <h4 className="text-sm font-semibold text-foreground">{selectedPost.title}</h4>
              <p className="text-xs text-muted-foreground mt-1">{selectedPost.client} · {selectedPost.page}</p>
              <p className="text-xs text-muted-foreground mt-1">{selectedPost.scheduledDate} at {selectedPost.scheduledTime}</p>
              <p className="text-sm text-foreground mt-2 leading-relaxed">{selectedPost.copy}</p>
            </div>
            <button onClick={() => setSelectedPost(null)} className="text-muted-foreground hover:text-foreground ml-4">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}